package com.devi3east.bashbuddy.features.tabs.ui

import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.NavigationBar
import androidx.compose.material3.NavigationBarItem
import androidx.compose.material3.NavigationBarItemDefaults
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.navigation.NavDestination.Companion.hierarchy
import androidx.navigation.NavHostController
import androidx.navigation.compose.currentBackStackEntryAsState
import com.devi3east.bashbuddy.auth.domain.models.UserType
import com.devi3east.bashbuddy.auth.ui.AuthViewModel
import com.devi3east.bashbuddy.features.app.data.AppState
import com.devi3east.bashbuddy.features.app.data.Screen
import com.devi3east.bashbuddy.features.common.ui.components.BashBuddyTopBar
import com.devi3east.bashbuddy.features.common.ui.components.Loader
import com.devi3east.bashbuddy.features.events.feed.ui.EventFeedViewModel
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@Composable
fun TabsScreen(
    appState: AppState,
    navController: NavHostController,
    eventFeedViewModel: EventFeedViewModel,
    authViewModel: AuthViewModel,
    content: @Composable (Modifier) -> Unit
) {
    val navBackStackEntry by navController.currentBackStackEntryAsState()
    val currentDestination = navBackStackEntry?.destination

    val leaderboardVisible = currentDestination
        ?.hierarchy
        ?.any { it.route == Screen.EventsScreen.route } == true

    val authUiState by authViewModel.uiState.collectAsState()

    val isHydrating = authUiState.isHydrating

    val visibleTabItems by remember(authUiState.profile, isHydrating) {
        derivedStateOf {
            val profile = authUiState.profile

            when {
                isHydrating -> emptyList()

                profile == null -> listOf(
                    Screen.EventsScreen,
                    Screen.SearchScreen
                )

                profile.userType == UserType.ORGANIZATION -> listOf(
                    Screen.EventsScreen,
                    Screen.DashboardScreen,
                    Screen.OrganizationScreen
                )

                else -> listOf(
                    Screen.EventsScreen,
                    Screen.SearchScreen,
                    Screen.FavoriteEventsScreen,
                    Screen.DashboardScreen,
                    Screen.ProfileScreen
                )
            }
        }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
        contentColor = MaterialTheme.colorScheme.onBackground,
        topBar = {
            if (!authUiState.isHydrating) {
                BashBuddyTopBar(
                    appState = appState,
                    profile = authUiState.profile,
                    leaderBoardVisible = leaderboardVisible
                )
            }
        },
        bottomBar = {
            if (!authUiState.isHydrating) {
                NavigationBar(
                    containerColor = MaterialTheme.colorScheme.onPrimary
                ) {
                    visibleTabItems.forEach { topLevelRoute ->

                        val isSelected =
                            currentDestination?.hierarchy?.any {
                                it.route == topLevelRoute.route
                            } == true

                        NavigationBarItem(
                            colors = NavigationBarItemDefaults.colors(
                                indicatorColor = Color.Transparent
                            ),
                            icon = {
                                val icon =
                                    if (isSelected) topLevelRoute.selectedIcon
                                    else topLevelRoute.unselectedIcon

                                icon?.let {
                                    Icon(
                                        tint = if (isSelected)
                                            MaterialTheme.colorScheme.primaryContainer
                                        else
                                            MaterialTheme.colorScheme.onBackground,
                                        modifier = Modifier.size(20.dp),
                                        painter = painterResource(icon),
                                        contentDescription = topLevelRoute.route
                                    )
                                }
                            },
                            label = { Text(stringResource(topLevelRoute.resourceId)) },
                            selected = isSelected,
                            onClick = {
                                if (topLevelRoute.route == Screen.EventsScreen.route) {
                                    eventFeedViewModel.getEventsList()
                                }

                                navController.navigate(topLevelRoute.route) {
                                    popUpTo(Screen.EventsScreen.route) {
                                        inclusive = false
                                    }
                                    launchSingleTop = true
                                    restoreState = false
                                }
                            }
                        )
                    }
                }
            }
        }
    ) { innerPadding ->
        if (authUiState.isHydrating) {
            Loader()
        } else {
            content(Modifier.padding(innerPadding))
        }
    }
}